import React from 'react';

import { Alert, Box, Paper } from '@mui/material';

import { FullscreenLoader } from 'src/components/common';
import type { SxProp } from 'src/types/mui';
import { composeSx } from 'src/utils/styles';

type JsonRendererProps = {
  src: string | undefined;
  containerStyles?: SxProp;
};

type FetchState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; content: string };

// Api verification reports are stored as JSON files rather than rendered PDFs, so we fetch the
// file from its (signed) download URL and pretty-print the payload.
export const JsonRenderer: React.FC<JsonRendererProps> = ({ src, containerStyles = {} }) => {
  const [state, setState] = React.useState<FetchState>({ status: 'loading' });

  React.useEffect(() => {
    if (!src) return;

    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) throw new Error(`Failed to load report: ${response.status}`);
        const data = await response.json();
        if (!cancelled) {
          setState({ status: 'loaded', content: JSON.stringify(data, null, 2) });
        }
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <Paper variant="outlined" sx={composeSx(defaultContainerStyles, containerStyles)}>
      {state.status === 'loading' && <FullscreenLoader sx={{ height: 500 }} />}
      {state.status === 'error' && (
        <Box p={2}>
          <Alert severity="error">Failed to load the report. Please try again later.</Alert>
        </Box>
      )}
      {state.status === 'loaded' && (
        <Box component="pre" sx={preStyles}>
          {state.content}
        </Box>
      )}
    </Paper>
  );
};

const defaultContainerStyles: SxProp = {
  maxHeight: '725px',
  overflow: 'auto',
  borderRadius: 2,
};

const preStyles: SxProp = {
  m: 0,
  p: 2,
  fontFamily: 'monospace',
  fontSize: '0.8125rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
